import { useEffect } from 'react';

const CHECK_EVERY_MS = 15 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
/** Do not reload a tab the user is still working in (invoice entry, open dialogs). */
const MIN_IDLE_BEFORE_RELOAD_MS = 60 * 60 * 1000;

const ACTIVITY_EVENTS = ['keydown', 'mousedown', 'touchstart', 'input', 'scroll'];

function uiIsBusy() {
    return Boolean(document.querySelector(
        '[role="dialog"], [aria-modal="true"], .modal-overlay, .modal-password-overlay'
    ));
}

/**
 * If the kiosk/tab was left open (or the browser restored a cached index.html),
 * reload once when the deployed SPA build id changes.
 * Waits until the user has been idle and no modal is open, so a long invoice
 * entry is not wiped by a deploy.
 */
export function useDeployReload() {
    useEffect(() => {
        if (import.meta.env.DEV) return;

        const currentId = typeof __APP_BUILD_ID__ !== 'undefined' ? String(__APP_BUILD_ID__) : '';
        if (!currentId) return;

        let checking = false;
        let lastCheck = 0;
        let lastActivity = Date.now();

        const markActivity = () => {
            lastActivity = Date.now();
        };

        const canReloadNow = () => {
            if (uiIsBusy()) return false;
            return Date.now() - lastActivity >= MIN_IDLE_BEFORE_RELOAD_MS;
        };

        const check = async (force = false) => {
            if (checking || document.visibilityState === 'hidden') return;
            const now = Date.now();
            if (!force && now - lastCheck < MIN_GAP_MS) return;
            lastCheck = now;
            checking = true;
            try {
                const res = await fetch(`/version.json?t=${now}`, { cache: 'no-store' });
                if (!res.ok) return;
                const data = await res.json();
                const remoteId = data?.buildId ? String(data.buildId) : '';
                if (!remoteId || remoteId === currentId) return;
                const guardKey = `erp-reload-for-${remoteId}`;
                if (sessionStorage.getItem(guardKey)) return;
                if (!canReloadNow()) return;
                sessionStorage.setItem(guardKey, '1');
                window.location.reload();
            } catch {
                // Ignore network errors; next scheduled check will retry.
            } finally {
                checking = false;
            }
        };

        const onVisible = () => {
            if (document.visibilityState === 'visible') check(false);
        };

        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
        document.addEventListener('visibilitychange', onVisible);
        const interval = setInterval(() => check(true), CHECK_EVERY_MS);
        check(true);

        return () => {
            ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(interval);
        };
    }, []);
}
